import { useMemo } from 'react';
import { createRoute } from '@tanstack/react-router';
import { InternalUiRenderer, createStateStore } from '@erp/json-renderer/react';
import type { StateStore } from '@erp/json-renderer/react';
import { useToast } from '@/context/ToastContext';
import { portalAuthenticatedRoute } from './route';
import { cannedPortalSpec, cannedPortalState } from './cannedPortal';

/**
 * The portal dashboard — renders the canned json-render spec with the
 * customer's brand applied by the surrounding `PortalShell`.
 *
 * The spec is interactive: buttons bind `on.press` to `markPaid` /
 * `resendInvoice` / `reorder`. We back the renderer with a controlled state
 * store; each handler reads the live snapshot, mutates it via `store.update`,
 * and the page re-renders (status badges, KPI totals, activity feed).
 */
export const portalDashboardRoute = createRoute({
  path: '/portal',
  getParentRoute: () => portalAuthenticatedRoute,
  component: PortalDashboardPage,
});

interface InvoiceLine {
  item: string;
  qty: number;
  amount: string;
}
interface InvoiceRow {
  invoice: string;
  amount: string;
  amountValue: number;
  status: string;
  statusVariant: string;
  paid: boolean;
  resent: boolean;
  resendLabel: string;
  dueLabel?: string;
  po?: string;
  issueDate?: string;
  terms?: string;
  paymentMethod?: string;
  billTo?: string;
  billToAddress?: string;
  subtotal?: string;
  tax?: string;
  total?: string;
  overdue?: boolean;
  overdueNote?: string;
  lineItems?: InvoiceLine[];
}
interface OrderRow {
  order: string;
  date: string;
  items: number;
  status: string;
  total: string;
}
interface ActivityRow {
  when: string;
  event: string;
}
interface PortalState {
  kpis: {
    activeOrders: string;
    openInvoicesTotal: string;
    openInvoicesBadge: string;
    openInvoicesBadgeVariant: string;
  };
  invoices: InvoiceRow[];
  orders: OrderRow[];
  activity: ActivityRow[];
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

/** Derive the "Open invoices" KPI (total + overdue badge) from the invoices. */
function recomputeInvoiceKpi(invoices: InvoiceRow[]): {
  total: string;
  badge: string;
  variant: string;
} {
  const openLike = invoices.filter(
    (inv) => inv.status === 'Open' || inv.status === 'Overdue',
  );
  const total = openLike.reduce((sum, inv) => sum + inv.amountValue, 0);
  const overdue = invoices.filter((inv) => inv.status === 'Overdue').length;
  return {
    total: usd.format(total),
    badge: overdue > 0 ? `${overdue} overdue` : 'All current',
    variant: overdue > 0 ? 'warning' : 'success',
  };
}

function PortalDashboardPage() {
  const { showToast } = useToast();
  const store: StateStore = useMemo(
    () => createStateStore(structuredClone(cannedPortalState)),
    [],
  );

  const handlers = useMemo(() => {
    function snapshot(): PortalState {
      return store.getSnapshot() as unknown as PortalState;
    }

    return {
      // Fill the slide-in's backing state from the picked invoice and open it.
      // The Sheet element binds `open` two-way, so closing it resets to false.
      openInvoice: (params: Record<string, unknown>) => {
        const id = String(params.invoice);
        const inv = snapshot().invoices.find((row) => row.invoice === id);
        if (!inv) return;
        store.update({
          '/invoiceSheet': {
            open: true,
            title: `Invoice ${inv.invoice}`,
            amount: inv.amount,
            status: inv.status,
            statusVariant: inv.statusVariant,
            dueLabel: inv.dueLabel ?? '',
            po: inv.po ?? '—',
            issueDate: inv.issueDate ?? '—',
            terms: inv.terms ?? '—',
            paymentMethod: inv.paymentMethod ?? '—',
            billTo: inv.billTo ?? '',
            billToAddress: inv.billToAddress ?? '',
            subtotal: inv.subtotal ?? inv.amount,
            tax: inv.tax ?? '$0.00',
            total: inv.total ?? inv.amount,
            overdue: inv.overdue ?? false,
            overdueNote: inv.overdueNote ?? '',
            lineItems: inv.lineItems ?? [],
          },
        });
      },

      markPaid: (params: Record<string, unknown>) => {
        const id = String(params.invoice);
        const snap = snapshot();
        const invoices = snap.invoices.map((inv) =>
          inv.invoice === id
            ? { ...inv, status: 'Paid', statusVariant: 'success', paid: true }
            : inv,
        );
        const kpi = recomputeInvoiceKpi(invoices);
        store.update({
          '/invoices': invoices,
          '/kpis/openInvoicesTotal': kpi.total,
          '/kpis/openInvoicesBadge': kpi.badge,
          '/kpis/openInvoicesBadgeVariant': kpi.variant,
          '/activity': [
            { when: 'Just now', event: `Marked ${id} as paid` },
            ...snap.activity,
          ],
        });
        showToast({
          title: 'Invoice paid',
          message: `${id} marked as paid.`,
          color: 'success',
        });
      },

      resendInvoice: (params: Record<string, unknown>) => {
        const id = String(params.invoice);
        const snap = snapshot();
        const invoices = snap.invoices.map((inv) =>
          inv.invoice === id
            ? { ...inv, resent: true, resendLabel: 'Sent' }
            : inv,
        );
        store.update({
          '/invoices': invoices,
          '/activity': [
            { when: 'Just now', event: `Requested a resend of ${id}` },
            ...snap.activity,
          ],
        });
        showToast({
          title: 'Resend requested',
          message: `We’ll email a copy of ${id} to your billing contact.`,
          color: 'info',
        });
      },

      reorder: (params: Record<string, unknown>) => {
        const id = String(params.order);
        const snap = snapshot();
        const source = snap.orders.find((order) => order.order === id);
        const newOrder: OrderRow = {
          order: `${id}-R`,
          date: 'Just now',
          items: source?.items ?? 1,
          status: 'Processing',
          total: source?.total ?? '$0.00',
        };
        const activeOrders = String(Number(snap.kpis.activeOrders) + 1);
        store.update({
          '/orders': [newOrder, ...snap.orders],
          '/kpis/activeOrders': activeOrders,
          '/activity': [
            { when: 'Just now', event: `Reordered ${id}` },
            ...snap.activity,
          ],
        });
        showToast({
          title: 'Reorder placed',
          message: `${id} reordered — now processing.`,
          color: 'success',
        });
      },
    };
  }, [store, showToast]);

  return (
    <InternalUiRenderer
      spec={cannedPortalSpec}
      store={store}
      handlers={handlers}
    />
  );
}
